import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MicIcon, UserPlusIcon, MapPinIcon, CheckIcon, ChevronRightIcon, SearchIcon, PlusIcon, SquareIcon, ChevronLeftIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { AssociateButton } from '../components/AssociateButton';
import { useScreenInit } from '../useScreenInit';
import { useLanguage } from '../components/LanguageContext';
type Message = {
  role: 'user' | 'ai';
  text: string;
};
type Item = {
  id: number;
  name: string;
  aisle: string;
  checked: boolean;
  thumb: string;
};
const initialItems: Item[] = [{
  id: 1,
  name: '4x4 Cedar Posts',
  aisle: 'Aisle 4',
  checked: false,
  thumb: 'bg-amber-800'
}, {
  id: 2,
  name: 'Joist Hangers',
  aisle: 'Aisle 12',
  checked: false,
  thumb: 'bg-slate-400'
}, {
  id: 3,
  name: '3" Deck Screws',
  aisle: 'Aisle 7',
  checked: false,
  thumb: 'bg-zinc-600'
}, {
  id: 4,
  name: 'Pressure Treated Boards',
  aisle: 'Aisle 18',
  checked: false,
  thumb: 'bg-amber-600'
}, {
  id: 5,
  name: 'Wood Sealant',
  aisle: 'Aisle 9',
  checked: false,
  thumb: 'bg-orange-200'
}];
// Lightweight "product directory" so typing/speaking a known product
// drops it in with a realistic aisle.
const productDirectory: Record<string, {
  aisle: string;
  thumb: string;
  name: string;
}> = {
  hammer: {
    aisle: 'Aisle 22',
    thumb: 'bg-zinc-700',
    name: 'Claw Hammer'
  },
  drill: {
    aisle: 'Aisle 21',
    thumb: 'bg-yellow-500',
    name: 'Cordless Drill'
  },
  paint: {
    aisle: 'Aisle 15',
    thumb: 'bg-rose-300',
    name: 'Interior Paint'
  },
  brush: {
    aisle: 'Aisle 15',
    thumb: 'bg-amber-300',
    name: 'Paint Brush'
  },
  nails: {
    aisle: 'Aisle 7',
    thumb: 'bg-slate-500',
    name: 'Finishing Nails'
  },
  screws: {
    aisle: 'Aisle 7',
    thumb: 'bg-zinc-600',
    name: 'Wood Screws'
  },
  saw: {
    aisle: 'Aisle 23',
    thumb: 'bg-red-500',
    name: 'Circular Saw'
  },
  pliers: {
    aisle: 'Aisle 22',
    thumb: 'bg-blue-500',
    name: 'Needle-Nose Pliers'
  },
  tape: {
    aisle: 'Aisle 22',
    thumb: 'bg-yellow-400',
    name: 'Measuring Tape'
  },
  sandpaper: {
    aisle: 'Aisle 9',
    thumb: 'bg-amber-200',
    name: 'Sandpaper Pack'
  },
  caulk: {
    aisle: 'Aisle 9',
    thumb: 'bg-slate-200',
    name: 'Silicone Caulk'
  },
  pvc: {
    aisle: 'Aisle 31',
    thumb: 'bg-slate-100',
    name: 'PVC Pipe'
  },
  pipe: {
    aisle: 'Aisle 31',
    thumb: 'bg-slate-100',
    name: 'PVC Pipe'
  },
  wire: {
    aisle: 'Aisle 28',
    thumb: 'bg-orange-500',
    name: 'Electrical Wire'
  },
  bulb: {
    aisle: 'Aisle 29',
    thumb: 'bg-yellow-200',
    name: 'LED Light Bulb'
  },
  shovel: {
    aisle: 'Aisle 35',
    thumb: 'bg-emerald-700',
    name: 'Garden Shovel'
  },
  mulch: {
    aisle: 'Aisle 36',
    thumb: 'bg-amber-700',
    name: 'Bag of Mulch'
  },
  concrete: {
    aisle: 'Aisle 19',
    thumb: 'bg-stone-400',
    name: 'Concrete Mix'
  },
  lumber: {
    aisle: 'Aisle 18',
    thumb: 'bg-amber-600',
    name: 'Pine Lumber'
  }
};
function resolveProduct(query: string): {
  name: string;
  aisle: string;
  thumb: string;
} {
  const q = query.trim().toLowerCase();
  for (const key of Object.keys(productDirectory)) {
    if (q.includes(key)) {
      return productDirectory[key];
    }
  }
  // Fallback: capitalize input and assign a plausible aisle
  const fallbackAisles = ['Aisle 5', 'Aisle 11', 'Aisle 14', 'Aisle 20', 'Aisle 26'];
  const fallbackThumbs = ['bg-slate-400', 'bg-blue-400', 'bg-emerald-400', 'bg-amber-400', 'bg-rose-400'];
  const aisle = fallbackAisles[query.length % fallbackAisles.length];
  const thumb = fallbackThumbs[query.length % fallbackThumbs.length];
  const name = query.trim().replace(/\b\w/g, c => c.toUpperCase());
  return {
    name,
    aisle,
    thumb
  };
}
export function ShoppingList() {
  useScreenInit();
  const navigate = useNavigate();
  const {
    t
  } = useLanguage();
  const [items, setItems] = useState<Item[]>(initialItems);
  const [showAssociateConfirm, setShowAssociateConfirm] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  // Items "heard" when the user speaks, added on stop.
  const dictatedQueue = ['paint', 'paint brush', 'painters tape', 'drop cloth'];
  const addDictatedItems = () => {
    const resolvedItems: Item[] = dictatedQueue.map((raw, i) => {
      const resolved = resolveProduct(raw);
      return {
        id: Date.now() + i,
        name: resolved.name,
        aisle: resolved.aisle,
        checked: false,
        thumb: resolved.thumb
      };
    });
    setItems(prev => [...resolvedItems, ...prev]);
    const firstId = resolvedItems[0]?.id ?? null;
    setHighlightId(firstId);
    setTimeout(() => setHighlightId(null), 2500);
  };
  const handleMicTap = () => {
    if (isRecording) {
      setIsRecording(false);
      setMessages(prev => [...prev, {
        role: 'user',
        text: t('list.chatUserMsg')
      }]);
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'ai',
          text: t('list.chatAiMsg')
        }]);
        addDictatedItems();
        // Add the items, then return to the shopping list automatically
        setTimeout(() => handleBackFromChat(), 1400);
      }, 900);
    } else {
      if (!isChatOpen) {
        setIsChatOpen(true);
        setTimeout(() => setIsRecording(true), 700);
      } else {
        setIsRecording(true);
      }
    }
  };
  const handleBackFromChat = () => {
    setIsChatOpen(false);
    setIsRecording(false);
    setMessages([]);
  };
  const toggleItem = (id: number) => {
    setItems(items.map(item => item.id === id ? {
      ...item,
      checked: !item.checked
    } : item));
  };
  const addProduct = (raw: string) => {
    if (!raw.trim()) return;
    const resolved = resolveProduct(raw);
    const newItem: Item = {
      id: Date.now(),
      name: resolved.name,
      aisle: resolved.aisle,
      checked: false,
      thumb: resolved.thumb
    };
    setItems(prev => [newItem, ...prev]);
    setHighlightId(newItem.id);
    setQuery('');
    setTimeout(() => setHighlightId(null), 2000);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addProduct(query);
  };
  const handleAssociateRequest = () => {
    setShowAssociateConfirm(true);
    setTimeout(() => setShowAssociateConfirm(false), 4000);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="flex flex-col h-full bg-slate-50 relative overflow-hidden" data-id="element-173">
      <div className="px-6 py-5 bg-white border-b border-slate-100" data-id="element-174">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-175">
          {t('list.title')}
        </h1>
        <p className="text-slate-500 text-sm mt-1" data-id="element-176">{t('list.subtitle')}</p>
      </div>

      {/* Search + list slide up & out while recording */}
      <motion.div animate={{
      y: isChatOpen ? '-110%' : '0%',
      opacity: isChatOpen ? 0 : 1
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 260
    }} style={{
      pointerEvents: isChatOpen ? 'none' : 'auto'
    }} className="flex flex-col flex-1 min-h-0" data-id="element-177">
        {/* Search / Add */}
        <div className="px-4 pt-4" data-id="element-178">
          <form onSubmit={handleSubmit} className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl pl-4 pr-2 py-2 shadow-[0_2px_12px_rgba(15,23,42,0.04)] focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all" data-id="element-179">
            <SearchIcon size={18} className="text-slate-400 shrink-0" data-id="element-180" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('list.searchPlaceholder')} className="flex-1 bg-transparent outline-none text-[15px] text-slate-900 placeholder:text-slate-400 py-2" data-id="element-181" />
            {query.trim() ? <motion.button whileTap={{
            scale: 0.95
          }} type="submit" className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shrink-0 transition-colors" aria-label="Add to list" data-id="element-182">
                <PlusIcon size={18} data-id="element-183" />
              </motion.button> : <motion.button whileTap={{
            scale: 0.95
          }} type="button" onClick={() => navigate('/chat')} className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shrink-0 transition-colors" aria-label="Talk to AI" data-id="element-184">
                <MicIcon size={18} data-id="element-185" />
              </motion.button>}
          </form>
          <p className="text-xs text-slate-400 mt-2 px-1" data-id="element-186">
            {t('list.searchHint')}
          </p>
        </div>

        <div className="px-4 pt-4 pb-44 flex-1 overflow-y-auto" data-id="element-187">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1" data-id="element-188">
            {t('list.sectionTitle')}
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden" data-id="element-189">
            {items.length === 0 ? <div className="p-8 text-center" data-id="element-190">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3" data-id="element-191">
                  <SearchIcon size={20} className="text-slate-400" data-id="element-192" />
                </div>
                <p className="text-sm font-medium text-slate-700" data-id="element-193">
                  {t('list.empty')}
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-194">
                  {t('list.emptyHint')}
                </p>
              </div> : <div className="divide-y divide-slate-100" data-id="element-195">
                <AnimatePresence initial={false} data-id="element-196">
                  {items.map(item => <motion.div key={item.id} layout initial={{
                opacity: 0,
                y: -8,
                backgroundColor: 'rgba(219, 234, 254, 0.6)'
              }} animate={{
                opacity: 1,
                y: 0,
                backgroundColor: highlightId === item.id ? 'rgba(219, 234, 254, 0.6)' : item.checked ? 'rgba(248, 250, 252, 1)' : 'rgba(255, 255, 255, 1)'
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.25
              }} className="px-3 py-2 flex items-center gap-3" data-id="element-197">
                      <button onClick={() => toggleItem(item.id)} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${item.checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'}`} data-id="element-198">
                        {item.checked && <CheckIcon size={14} strokeWidth={3} data-id="element-199" />}
                      </button>

                      <div className="flex-1 flex items-center gap-2.5 cursor-pointer min-w-0" onClick={() => navigate('/product')} data-id="element-200">
                        <div className={`w-8 h-8 rounded-md shrink-0 ${item.thumb} opacity-80`} data-id="element-201" />
                        <div className="flex-1 min-w-0" data-id="element-202">
                          <p className={`text-[14px] font-medium truncate transition-colors ${item.checked ? 'text-slate-400 line-through' : 'text-slate-900'} leading-tight`} data-id="element-203">
                            {item.name}
                          </p>
                          <div className="flex items-center text-blue-600 text-[11px] font-medium mt-0.5" data-id="element-204">
                            <MapPinIcon size={10} className="mr-0.5" data-id="element-205" />
                            {item.aisle}
                          </div>
                        </div>
                        <ChevronRightIcon size={18} className="text-slate-300 shrink-0" data-id="element-206" />
                      </div>
                    </motion.div>)}
                </AnimatePresence>
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Recording overlay */}
      <AnimatePresence data-id="element-207">
        {isChatOpen && <motion.div key="chat-panel" initial={{
        y: '100%'
      }} animate={{
        y: 0,
        transition: {
          duration: 0.55,
          ease: [0.16, 1, 0.3, 1]
        }
      }} exit={{
        y: '-120%',
        transition: {
          duration: 0.55,
          ease: [0.7, 0, 0.84, 0]
        }
      }} className="absolute inset-x-0 top-[92px] bottom-[140px] bg-slate-50 flex flex-col overflow-y-auto pt-2 pb-4 space-y-3 px-4 z-10" data-id="element-208">
            <button onClick={handleBackFromChat} className="self-start flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label={t('list.back')} data-id="element-209">
              <ChevronLeftIcon size={18} data-id="element-210" />
              {t('list.back')}
            </button>
            {messages.map((msg, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-211">
                <div className={`max-w-[85%] rounded-2xl p-4 ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-slate-100 text-slate-900 rounded-tl-sm'}`} data-id="element-212">
                  <p className="text-[15px] leading-relaxed" data-id="element-213">{msg.text}</p>
                </div>
              </motion.div>)}
            {isRecording && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex justify-end" data-id="element-214">
                <div className="max-w-[85%] rounded-2xl p-4 bg-blue-600 text-white rounded-tr-sm flex items-center gap-2" data-id="element-215">
                  <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-216"></span>
                  <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-217"></span>
                  <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-218"></span>
                </div>
              </motion.div>}
          </motion.div>}
      </AnimatePresence>

      {/* Fixed Bottom Actions — solid darker gray footer that the list clips behind */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pt-4 pb-5 bg-slate-200 border-t border-slate-300 flex flex-col items-center gap-3 z-20" data-id="element-219">
        <p className={`text-sm font-medium transition-colors ${isRecording ? 'text-red-500' : 'text-slate-600'}`} data-id="element-220">
          {isRecording ? t('list.tapToStop') : t('list.addMore')}
        </p>
        <motion.button type="button" whileTap={{
        scale: 0.95
      }} onClick={handleMicTap} aria-label={isRecording ? t('list.tapToStop') : t('list.addMore')} className={`relative w-20 h-20 rounded-full flex items-center justify-center text-white shadow-lg transition-colors ${isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-221">
          <motion.span aria-hidden="true" animate={{
          scale: [1, 1.45, 1],
          opacity: [0.45, 0, 0.45]
        }} transition={{
          repeat: Infinity,
          duration: isRecording ? 1.3 : 2,
          ease: 'easeInOut'
        }} className={`absolute w-20 h-20 rounded-full -z-10 ${isRecording ? 'bg-red-400' : 'bg-blue-400'}`} data-id="element-222" />
          {isRecording ? <SquareIcon size={28} fill="currentColor" data-id="element-223" /> : <MicIcon size={32} data-id="element-224" />}
        </motion.button>

        <AnimatePresence data-id="element-225">
          {!isChatOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.25
        }} className="w-full mt-1 overflow-hidden" data-id="element-226">
              <AssociateButton idleLabel={t('list.requestAssociate')} data-id="element-227" />
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}